import {
  type AuditLogEntry,
  type BranchManualPaymentChannel,
  type DashboardProductListQuery,
  type DashboardProductListResponse,
  isAuditCategory,
  type ManualPaymentChannel,
} from '@pos/types';

import { dashboardApi } from '../lib/apiClient';
import { bangkokBusinessDay } from '../lib/businessDate';
import { dayIndexToMs, type DayRange } from '../lib/day';
import type {
  AuditAction,
  AuditEntry,
  Branch,
  BranchPaymentSettings,
  BranchPricing,
  CashRound,
  Category,
  PaymentMethodConfig,
  Product,
  Promotion,
  SaleTxn,
  StockMovement,
  StockRow,
  SystemSettings,
  User,
} from '../types/domain';

export {
  getPromotionProductsByIds,
  searchPromotionProducts,
} from './promotionProducts';

export interface BranchScope {
  branchId: string | null;
  allowedBranchIds?: string[];
}

const toIsoDateFromDay = (day: number): string =>
  new Date(dayIndexToMs(day)).toISOString().slice(0, 10);

const toMonthLabel = (value: string): string => {
  const month = new Date(`${value}T00:00:00.000Z`).getUTCMonth();
  return (
    [
      'ม.ค.',
      'ก.พ.',
      'มี.ค.',
      'เม.ย.',
      'พ.ค.',
      'มิ.ย.',
      'ก.ค.',
      'ส.ค.',
      'ก.ย.',
      'ต.ค.',
      'พ.ย.',
      'ธ.ค.',
    ][month] ?? value.slice(0, 7)
  );
};

const monthNameToRange = (monthName: string): { from: string; to: string } => {
  const labels = [
    'ม.ค.',
    'ก.พ.',
    'มี.ค.',
    'เม.ย.',
    'พ.ค.',
    'มิ.ย.',
    'ก.ค.',
    'ส.ค.',
    'ก.ย.',
    'ต.ค.',
    'พ.ย.',
    'ธ.ค.',
  ];
  const month = Math.max(0, labels.indexOf(monthName));
  const year = new Date(dayIndexToMs(bangkokBusinessDay())).getUTCFullYear();
  return {
    from: new Date(Date.UTC(year, month, 1)).toISOString().slice(0, 10),
    to: new Date(Date.UTC(year, month + 1, 0)).toISOString().slice(0, 10),
  };
};

export const listUsers = (): Promise<User[]> => dashboardApi.listUsers();
export const listPaymentMethods = (): Promise<PaymentMethodConfig[]> =>
  dashboardApi.listPaymentMethods();
export const listBranchPaymentSettings = (): Promise<BranchPaymentSettings[]> =>
  dashboardApi.listBranchPaymentSettings();
export const listManualPaymentChannels = (): Promise<ManualPaymentChannel[]> =>
  dashboardApi.listManualPaymentChannels();
export const listBranchManualPaymentChannels = (): Promise<
  BranchManualPaymentChannel[]
> => dashboardApi.listBranchManualPaymentChannels();
export const listPromotions = (): Promise<Promotion[]> =>
  dashboardApi.listPromotions();
export const getSettings = (): Promise<SystemSettings> =>
  dashboardApi.listSettings();
export const listBranches = (): Promise<Branch[]> =>
  dashboardApi.listBranches();
export const listProducts = (): Promise<Product[]> =>
  dashboardApi.listProducts();
export const listCategories = (): Promise<Category[]> =>
  dashboardApi.listCategories();
export const listBranchPricing = (): Promise<BranchPricing[]> =>
  dashboardApi.listBranchPricing();

export interface AuditQuery {
  page: number;
  pageSize: number;
  branchId: string | null;
  action?: AuditEntry['action'] | '';
  allowedBranchIds: string[];
}

const AUDIT_ENTITY_TH: Record<string, string> = {
  products: 'สินค้า',
  product_units: 'หน่วยสินค้า',
  product_barcodes: 'บาร์โค้ด',
  branch_prices: 'ราคาสาขา',
  categories: 'หมวดหมู่',
  payment_methods: 'วิธีชำระ',
  branch_payment_settings: 'ตั้งค่าชำระสาขา',
  branches: 'สาขา',
  users: 'ผู้ใช้',
  devices: 'อุปกรณ์',
  settings: 'ตั้งค่า',
  promotions: 'โปรโมชั่น',
  stock: 'สต๊อก',
};

const mapApiAuditAction = (entry: AuditLogEntry): AuditAction => {
  if (entry.action === 'sale.line_discount_applied') return 'discount-applied';
  if (
    entry.action === 'device.revoke' ||
    entry.action === 'device.recovery_code.create'
  )
    return 'device-management';
  if (entry.action === 'stock.adjust' || entry.action === 'stock.receive')
    return 'stock-adjust';
  if (entry.entity === 'branch_prices') return 'price-edit';
  if (
    entry.action === 'master_data.create' &&
    ['products', 'product_units', 'product_barcodes'].includes(entry.entity)
  )
    return 'product-create';
  return 'master-data';
};

const mapApiAuditDetail = (entry: AuditLogEntry): string => {
  if (entry.display?.title) {
    return entry.display.detail
      ? `${entry.display.title} · ${entry.display.detail}`
      : entry.display.title;
  }
  const payload = entry.payload ?? {};
  const label = AUDIT_ENTITY_TH[entry.entity] ?? entry.entity;
  if (entry.action === 'device.revoke')
    return `เพิกถอนเครื่อง · ${entry.entityId}`;
  if (entry.action === 'device.recovery_code.create')
    return `สร้างรหัสกู้คืนเครื่อง · ${entry.entityId}`;
  if (entry.entity === 'stock') {
    const quantity =
      typeof payload.quantity === 'number' ? payload.quantity : undefined;
    const reason = typeof payload.reason === 'string' ? payload.reason : '';
    return `${label} ${entry.entityId}${quantity === undefined ? '' : ` (${quantity > 0 ? '+' : ''}${quantity})`}${reason ? ` · ${reason}` : ''}`;
  }
  const verb =
    entry.action === 'master_data.create'
      ? 'สร้าง'
      : entry.action === 'master_data.delete'
        ? 'ลบ'
        : 'แก้ไข';
  return `${verb}${label} · ${entry.entityId}`;
};

const mapApiAudit = (entry: AuditLogEntry, index: number): AuditEntry => ({
  id: `${entry.timestamp}-${entry.entity}-${entry.entityId}-${index}`,
  at: new Date(entry.timestamp).getTime(),
  user: entry.display?.actorName ?? entry.actorUserId,
  action: mapApiAuditAction(entry),
  detail: mapApiAuditDetail(entry),
  branchId: entry.branchId ?? 'global',
});

export async function listAudit(
  query: AuditQuery,
): Promise<{ rows: AuditEntry[]; total: number }> {
  if (query.action && !isAuditCategory(query.action))
    return { rows: [], total: 0 };
  const { entries, total } = await dashboardApi.listAudit({
    limit: query.pageSize,
    offset: (query.page - 1) * query.pageSize,
    category: query.action || undefined,
    branchId: query.branchId || undefined,
  });
  return { rows: entries.map(mapApiAudit), total };
}

export interface ProductListPageQuery extends Pick<
  DashboardProductListQuery,
  'search' | 'categoryId' | 'status'
> {
  page: number;
  pageSize: number;
}

export const listProductsPage = (
  query: ProductListPageQuery,
): Promise<DashboardProductListResponse> =>
  dashboardApi.listProductsPage({
    limit: query.pageSize,
    offset: (query.page - 1) * query.pageSize,
    search: query.search || undefined,
    categoryId: query.categoryId || undefined,
    status: query.status ?? 'all',
  });

export async function getDailySales(
  scope: BranchScope,
  range?: DayRange,
): Promise<{ txns: SaleTxn[] }> {
  const resolved = range ?? {
    fromDay: bangkokBusinessDay(),
    toDay: bangkokBusinessDay(),
  };
  const receipts = [];
  let offset = 0;
  let hasMore = true;
  while (hasMore) {
    const result = await dashboardApi.listReceipts({
      from: toIsoDateFromDay(resolved.fromDay),
      to: toIsoDateFromDay(resolved.toDay),
      branchId: scope.branchId ?? undefined,
      limit: 500,
      offset,
    });
    receipts.push(...result.receipts);
    hasMore = result.hasMore && result.receipts.length > 0;
    offset += result.receipts.length;
  }
  return {
    txns: receipts.map((receipt) => ({
      id: receipt.saleId,
      branchId: receipt.branchId,
      at: Date.parse(receipt.soldAt),
      total: receipt.totalAmount,
      method: receipt.paymentMethods.includes('cash') ? 'cash' : 'qr',
      status: receipt.status === 'voided' ? 'refund' : 'normal',
      items: [],
    })),
  };
}

const branchIdsFor = async (scope: BranchScope): Promise<string[]> => {
  if (scope.branchId) return [scope.branchId];
  if (scope.allowedBranchIds) return scope.allowedBranchIds;
  return (await dashboardApi.listBranches()).map((branch) => branch.id);
};

export async function listStockByBranch(
  scope: BranchScope,
): Promise<StockRow[]> {
  return (
    await Promise.all(
      (await branchIdsFor(scope)).map((branchId) =>
        dashboardApi.listStockByBranch({ branchId }),
      ),
    )
  ).flat();
}

export async function listMovements(
  scope: BranchScope,
): Promise<StockMovement[]> {
  return (
    await Promise.all(
      (await branchIdsFor(scope)).map((branchId) =>
        dashboardApi.listStockMovements({ branchId }),
      ),
    )
  ).flat();
}

export function listMovementsPage(input: {
  scope: BranchScope;
  before?: string;
  limit: number;
}): Promise<{
  movements: StockMovement[];
  hasMore: boolean;
  nextCursor?: string;
}> {
  return dashboardApi.listStockMovementsPage({
    branchId: input.scope.branchId ?? undefined,
    before: input.before,
    limit: input.limit,
  });
}

export async function getMonthlySales(scope: BranchScope): Promise<{
  labels: string[];
  series: number[];
  forecast: number;
  gross: number;
}> {
  const year = new Date(dayIndexToMs(bangkokBusinessDay())).getUTCFullYear();
  const report = await dashboardApi.getSalesReport({
    from: `${year}-01-01`,
    to: toIsoDateFromDay(bangkokBusinessDay()),
    branchId: scope.branchId ?? undefined,
  });
  const series = report.monthly.map((row) => row.total);
  const last3 = series.slice(-3);
  return {
    labels: report.monthly.map((row) => toMonthLabel(row.date)),
    series,
    forecast: Math.round(
      last3.reduce((sum, value) => sum + value, 0) / Math.max(1, last3.length),
    ),
    gross: report.total,
  };
}

export interface MonthlyBranchSales {
  branchId: string;
  branchName: string;
  revenue: number;
  mom: number;
}
export interface MonthlyCategorySales {
  categoryId: string;
  categoryName: string;
  amount: number;
}
export interface MonthlyProductSales {
  productId: string;
  name: string;
  qty: number;
  amount: number;
}
export interface MonthlySalesDetailed {
  month: string;
  grossBeforeDiscount: number;
  gross: number;
  promotionDiscount: number;
  billDiscount: number;
  roundingDelta: number;
  legacyAccountingReceiptCount: number;
  cogs: number;
  grossProfit: number;
  promotions: Array<{
    promotionId: string;
    promotionName: string;
    receiptCount: number;
    discountAmount: number;
    freeQuantity: number;
  }>;
  branches: MonthlyBranchSales[];
  categories: MonthlyCategorySales[];
  topProducts: MonthlyProductSales[];
  products: MonthlyProductSales[];
}

export async function getMonthlySalesDetailed(
  monthName: string,
  scope: BranchScope,
): Promise<MonthlySalesDetailed> {
  const range = monthNameToRange(monthName);
  const report = await dashboardApi.getSalesReport({
    ...range,
    branchId: scope.branchId ?? undefined,
  });
  const mapProduct = (
    row: (typeof report.topByAmount)[number],
  ): MonthlyProductSales => ({
    productId: row.productId,
    name: row.productName,
    qty: row.quantity,
    amount: row.amount,
  });
  return {
    month: monthName,
    grossBeforeDiscount: report.grossSales ?? report.total,
    gross: report.total,
    promotionDiscount: report.promotionDiscount ?? 0,
    billDiscount: report.billDiscount ?? 0,
    roundingDelta: report.roundingDelta ?? 0,
    legacyAccountingReceiptCount: report.legacyAccountingReceiptCount ?? 0,
    cogs: report.cogs,
    grossProfit: report.grossProfit,
    promotions: report.promotions ?? [],
    branches: report.byBranch.map((row) => ({
      branchId: row.branchId,
      branchName: row.branchName,
      revenue: row.total,
      mom:
        row.previousTotal > 0
          ? (row.total - row.previousTotal) / row.previousTotal
          : 0,
    })),
    categories: report.categories.map((row) => ({
      categoryId: row.categoryId,
      categoryName: row.categoryName,
      amount: row.amount,
    })),
    topProducts: report.topByQty.map(mapProduct),
    products: report.topByAmount.map(mapProduct),
  };
}

export interface BranchSalesRow {
  branchId: string;
  revenue: number;
  prevRevenue: number;
}
export interface BranchSalesResult {
  rows: BranchSalesRow[];
  total: number;
  prevTotal: number;
  days: number;
}

export async function getSalesByBranch(
  scope: BranchScope,
  range: DayRange,
  _previous?: DayRange,
): Promise<BranchSalesResult> {
  const report = await dashboardApi.getSalesReport({
    from: toIsoDateFromDay(range.fromDay),
    to: toIsoDateFromDay(range.toDay),
    branchId: scope.branchId ?? undefined,
  });
  return {
    rows: report.byBranch.map((row) => ({
      branchId: row.branchId,
      revenue: row.total,
      prevRevenue: row.previousTotal,
    })),
    total: report.total,
    prevTotal: report.previousTotal,
    days: range.toDay - range.fromDay + 1,
  };
}

export interface BranchDetail {
  series: { label: string; value: number }[];
  topProducts: { name: string; qty: number; amount: number }[];
}
export async function getBranchDetail(
  branchId: string,
  range: DayRange,
): Promise<BranchDetail> {
  const report = await dashboardApi.getSalesReport({
    from: toIsoDateFromDay(range.fromDay),
    to: toIsoDateFromDay(range.toDay),
    branchId,
  });
  return {
    series: report.daily.map((row) => ({
      label: row.date.slice(-2),
      value: row.total,
    })),
    topProducts: report.topByAmount.map((row) => ({
      name: row.productName,
      qty: row.quantity,
      amount: row.amount,
    })),
  };
}

export interface DailyAnalytics {
  series: { label: string; value: number }[];
  total: number;
  prevTotal: number;
  days: number;
  byHour: { label: string; value: number }[];
  payment: { label: string; value: number }[];
  bestDay: { label: string; value: number } | null;
}

export async function getDailyAnalytics(
  scope: BranchScope,
  range: DayRange,
  _previous?: DayRange,
): Promise<DailyAnalytics> {
  const report = await dashboardApi.getSalesReport({
    from: toIsoDateFromDay(range.fromDay),
    to: toIsoDateFromDay(range.toDay),
    branchId: scope.branchId ?? undefined,
  });
  const series = report.daily.map((row) => ({
    label: String(new Date(`${row.date}T00:00:00.000Z`).getUTCDate()),
    value: row.total,
  }));
  return {
    series,
    total: report.total,
    prevTotal: report.previousTotal,
    days: report.daily.length,
    byHour: [],
    payment: report.payments.map((row) => ({
      label: row.label,
      value: row.amount,
    })),
    bestDay: series.length
      ? series.reduce((best, row) => (row.value > best.value ? row : best))
      : null,
  };
}

export interface ProductSalesRow {
  productId: string;
  name: string;
  categoryId: string;
  baseUnitName: string;
  qty: number;
  amount: number;
  prevQty: number;
  prevAmount: number;
  cogs: number;
  profit: number;
  prevProfit: number;
  margin: number | null;
  missingCostLineCount: number;
}
export interface ProductSalesResult {
  rows: ProductSalesRow[];
  totalQty: number;
  totalAmount: number;
  prevTotalQty: number;
  prevTotalAmount: number;
  totalCogs: number;
  totalProfit: number;
  missingCostLineCount: number;
  activeSku: number;
  totalSku: number;
}

export async function getProductSales(
  scope: BranchScope,
  range: DayRange,
  _previous?: DayRange,
): Promise<ProductSalesResult> {
  const report = await dashboardApi.getSalesReport({
    from: toIsoDateFromDay(range.fromDay),
    to: toIsoDateFromDay(range.toDay),
    branchId: scope.branchId ?? undefined,
  });
  const rows = (report.products ?? report.topByAmount).map((row) => ({
    productId: row.productId,
    name: row.productName,
    categoryId: row.categoryId ?? '',
    baseUnitName: row.baseUnitName,
    qty: row.baseQuantity,
    amount: row.netAmount,
    prevQty: row.previousBaseQuantity,
    prevAmount: row.previousNetAmount,
    cogs: row.cogs,
    profit: row.grossProfit,
    prevProfit: row.previousGrossProfit,
    margin: row.margin,
    missingCostLineCount: row.missingCostLineCount,
  }));
  return {
    rows,
    totalQty: rows.reduce((sum, row) => sum + row.qty, 0),
    totalAmount: report.total,
    prevTotalQty: rows.reduce((sum, row) => sum + row.prevQty, 0),
    prevTotalAmount: report.previousTotal,
    totalCogs: report.cogs,
    totalProfit: report.grossProfit,
    missingCostLineCount: rows.reduce(
      (sum, row) => sum + row.missingCostLineCount,
      0,
    ),
    activeSku: rows.filter((row) => row.qty > 0).length,
    totalSku: rows.length,
  };
}

export interface ProductDetail {
  summary: {
    baseUnitName: string;
    baseQuantity: number;
    grossAmount: number;
    netAmount: number;
    cogs: number;
    profit: number;
    margin: number | null;
    missingCostLineCount: number;
  };
  series: { label: string; value: number }[];
  byBranch: {
    branchId: string;
    branchShort: string;
    qty: number;
    amount: number;
    cogs: number;
    profit: number;
  }[];
  byUnit: Array<{
    unitId: string;
    unitName: string;
    ratio: number;
    quantity: number;
    baseQuantity: number;
    grossAmount: number;
    netAmount: number;
    cogs: number;
    profit: number;
    margin: number | null;
    missingCostLineCount: number;
  }>;
}
export async function getProductDetail(
  productId: string,
  scope: BranchScope,
  range: DayRange,
): Promise<ProductDetail> {
  const detail = await dashboardApi.getProductSalesDetail({
    productId,
    from: toIsoDateFromDay(range.fromDay),
    to: toIsoDateFromDay(range.toDay),
    branchId: scope.branchId ?? undefined,
  });
  return {
    summary: {
      ...detail.summary,
      profit: detail.summary.grossProfit,
    },
    series: detail.daily.map((row) => ({
      label: row.date.slice(-2),
      value: row.total,
    })),
    byBranch: detail.byBranch.map((row) => ({
      branchId: row.branchId,
      branchShort: row.branchName,
      qty: row.baseQuantity,
      amount: row.amount,
      cogs: row.cogs,
      profit: row.grossProfit,
    })),
    byUnit: detail.byUnit.map((row) => ({
      ...row,
      profit: row.grossProfit,
    })),
  };
}

export async function getRoundsReport(
  scope: BranchScope,
): Promise<CashRound[]> {
  const report = await dashboardApi.getShiftReport({
    branchId: scope.branchId ?? undefined,
  });
  return report.rows.map((row) => ({
    id: row.shiftId,
    cashierName: row.cashierName,
    branchId: row.branchId,
    date: row.businessDate,
    openedAt: row.openedAt,
    closedAt: row.closedAt,
    expected: row.expectedCash,
    counted: row.countedCash ?? 0,
    diff: row.discrepancy ?? 0,
    status: row.settlement === 'open' ? 'open' : 'closed',
    payouts: row.payouts.map((payout) => ({
      method: payout.type,
      amount: payout.amount,
    })),
  }));
}

export interface VatMonthlyRow {
  month: string;
  gross: number;
  exVat: number;
  vat: number;
  status: string;
}
export async function getVatSummary(
  scope: BranchScope,
  vatRate: number,
): Promise<{
  gross: number;
  exVat: number;
  vat: number;
  rows: VatMonthlyRow[];
}> {
  const year = new Date(dayIndexToMs(bangkokBusinessDay())).getUTCFullYear();
  const report = await dashboardApi.getSalesReport({
    from: `${year}-01-01`,
    to: `${year}-12-31`,
    branchId: scope.branchId ?? undefined,
  });
  const rows = report.monthly.map((row) => {
    const exVat = Math.round(row.total / (1 + vatRate));
    return {
      month: toMonthLabel(row.date),
      gross: row.total,
      exVat,
      vat: row.total - exVat,
      status: 'open',
    };
  });
  return {
    gross: rows.reduce((sum, row) => sum + row.gross, 0),
    exVat: rows.reduce((sum, row) => sum + row.exVat, 0),
    vat: rows.reduce((sum, row) => sum + row.vat, 0),
    rows,
  };
}

export interface VatDetailedProductRow {
  productId: string;
  name: string;
  categoryId: string;
  qty: number;
  gross: number;
  exVat: number;
  vat: number;
}
export interface VatDetailedReport {
  month: string;
  gross: number;
  exVat: number;
  vat: number;
  rows: VatDetailedProductRow[];
}
export async function getVatDetailedReport(
  monthName: string,
  scope: BranchScope,
  vatRate: number,
): Promise<VatDetailedReport> {
  const range = monthNameToRange(monthName);
  const report = await dashboardApi.getSalesReport({
    ...range,
    branchId: scope.branchId ?? undefined,
  });
  const rows = (report.products ?? report.topByAmount).map((product) => {
    const exVat = Math.round(product.amount / (1 + vatRate));
    return {
      productId: product.productId,
      name: product.productName,
      categoryId: product.categoryId ?? '',
      qty: product.quantity,
      gross: product.amount,
      exVat,
      vat: product.amount - exVat,
    };
  });
  const gross = rows.reduce((sum, row) => sum + row.gross, 0);
  const exVat = rows.reduce((sum, row) => sum + row.exVat, 0);
  return { month: monthName, gross, exVat, vat: gross - exVat, rows };
}

export interface InventoryRow {
  productId: string;
  name: string;
  categoryId: string;
  branchId: string;
  branchShort: string;
  onHand: number;
  value: number;
  low: boolean;
}
export async function getInventoryReport(
  scope: BranchScope,
): Promise<{ rows: InventoryRow[]; lowCount: number; totalValue: number }> {
  const report = await dashboardApi.getInventoryReport({
    branchId: scope.branchId ?? undefined,
  });
  return {
    rows: report.rows.map((row) => ({
      productId: row.productId,
      name: row.productName,
      categoryId: row.categoryId ?? '',
      branchId: row.branchId,
      branchShort: row.branchName,
      onHand: row.onHand,
      value: row.value,
      low: row.low,
    })),
    lowCount: report.lowCount,
    totalValue: report.totalValue,
  };
}
